import { defaultCatalog, type Language, type Person, type Phrase } from './data.ts';

export const APP_VERSION = '1.2.2';
const DB_NAME = 'zyron-voice';
const STORE = 'state';
const DB_VERSION = 1;
const MIRROR_KEY = 'zyron-voice-state-backup-v1';
const STARTUP_PRIMARY_GRACE_MS = 250;
export const STORAGE_ATTEMPT_TIMEOUT_MS = 3_500;
export const STORAGE_MAX_ATTEMPTS = 3;

export type StorageIssue = 'abort' | 'blocked' | 'connection-lost' | 'error' | 'timeout' | 'unavailable' | 'version';

export class StorageFailure extends Error {
  readonly issue: StorageIssue;

  constructor(issue: StorageIssue, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'StorageFailure';
    this.issue = issue;
  }
}

export interface StorageRetryOptions {
  maxAttempts?: number;
  attemptTimeoutMs?: number;
  retryDelayMs?: number;
  onAttempt?: (attempt: number, maxAttempts: number) => void;
}

const openConnections = new Set<IDBDatabase>();
let saveQueue: Promise<unknown> = Promise.resolve();

export interface Settings {
  generalDwell: number;
  keyboardDwell: number;
  language: Language;
  englishVoice: string | null;
  arabicVoice: string | null;
  speechRate: number;
  autoSpeak: boolean;
  completedOnboarding: boolean;
  pinSalt: string;
  pinHash: string;
}

export interface AppState {
  schemaVersion: 1;
  appVersion: string;
  updatedAt: number;
  storageRevision: number;
  recoveryBase?: boolean;
  settings: Settings;
  emergency: Phrase[];
  bedroom: Phrase[];
  massage: Phrase[];
  tablets: Phrase[];
  people: Person[];
  englishDraft: string;
  arabicDraft: string;
}

export type StorageMode = 'indexeddb' | 'fallback' | 'temporary';
export interface LoadedState { state: AppState; mode: StorageMode; warning?: string }
export interface ReconciliationResult extends LoadedState { changed: boolean; conflict?: boolean }

interface StateMirror {
  format: 1 | 2;
  state: AppState;
  baseHash: string | null;
  pending: boolean;
  updatedAt: number;
}

function mirrorStorage(): Storage | null {
  try { return globalThis.localStorage ?? null; }
  catch { return null; }
}

function readMirrorRaw(): Partial<StateMirror> | null {
  try {
    const raw = mirrorStorage()?.getItem(MIRROR_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StateMirror>;
    if ((value.format !== 1 && value.format !== 2) || !value.state) return null;
    return value;
  } catch { return null; }
}

function writeMirror(mirror: StateMirror): void {
  try {
    const storage = mirrorStorage();
    if (!storage) throw new Error('localStorage is unavailable.');
    storage.setItem(MIRROR_KEY, JSON.stringify(mirror));
  } catch (error) {
    throw new StorageFailure('unavailable', 'The secondary local mirror could not be saved.', { cause: error });
  }
}

const bytesToHex = (bytes: Uint8Array) => [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
const hexToBytes = (hex: string) => new Uint8Array(hex.match(/.{1,2}/g)?.map((byte) => Number.parseInt(byte, 16)) ?? []);

async function derivePin(pin: string, salt: Uint8Array): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations: 120_000 }, key, 256);
  return bytesToHex(new Uint8Array(bits));
}

export async function createPin(pin: string): Promise<{ pinSalt: string; pinHash: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return { pinSalt: bytesToHex(salt), pinHash: await derivePin(pin, salt) };
}

export async function verifyPin(pin: string, settings: Settings): Promise<boolean> {
  return (await derivePin(pin, hexToBytes(settings.pinSalt))) === settings.pinHash;
}

async function defaults(completedOnboarding = false, recoveryBase = false): Promise<AppState> {
  const credentials = await createPin('2468');
  return {
    schemaVersion: 1,
    appVersion: APP_VERSION,
    updatedAt: Date.now(),
    storageRevision: 0,
    recoveryBase,
    settings: { generalDwell: 1, keyboardDwell: 1, language: 'en', englishVoice: null, arabicVoice: null, speechRate: 1, autoSpeak: true, completedOnboarding, ...credentials },
    emergency: structuredClone(defaultCatalog.emergency),
    bedroom: structuredClone(defaultCatalog.bedroom),
    massage: structuredClone(defaultCatalog.massage),
    tablets: structuredClone(defaultCatalog.tablets),
    people: structuredClone(defaultCatalog.people),
    englishDraft: '',
    arabicDraft: '',
  };
}

export async function safeDefaultState(): Promise<AppState> {
  return defaults(true, true);
}

function storageFailure(error: unknown, fallback: StorageIssue = 'error'): StorageFailure {
  if (error instanceof StorageFailure) return error;
  const name = error instanceof DOMException ? error.name : '';
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown IndexedDB error');
  if (name === 'AbortError') return new StorageFailure('abort', message, { cause: error });
  if (name === 'VersionError') return new StorageFailure('version', message, { cause: error });
  if (/connection.*(lost|clos)|database.*clos|invalidstate/i.test(`${name} ${message}`)) return new StorageFailure('connection-lost', message, { cause: error });
  return new StorageFailure(fallback, message, { cause: error });
}

function timeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = globalThis.setTimeout(() => reject(new StorageFailure('timeout', message)), timeoutMs);
    promise.then(
      (value) => { globalThis.clearTimeout(timer); resolve(value); },
      (error) => { globalThis.clearTimeout(timer); reject(error); },
    );
  });
}

const delay = (milliseconds: number) => new Promise<void>((resolve) => globalThis.setTimeout(resolve, milliseconds));

export function closeDatabaseConnections(): void {
  for (const database of openConnections) {
    try { database.close(); } catch { /* WebKit already closed it. */ }
  }
  openConnections.clear();
}

function trackConnection(database: IDBDatabase): IDBDatabase {
  openConnections.add(database);
  const forget = () => openConnections.delete(database);
  database.addEventListener('close', forget, { once: true });
  database.addEventListener('versionchange', () => {
    forget();
    try { database.close(); } catch { /* Already closing. */ }
  });
  return database;
}

function releaseConnection(database: IDBDatabase): void {
  openConnections.delete(database);
  try { database.close(); } catch { /* Already closed. */ }
}

export function openDatabase(factory: IDBFactory | undefined = globalThis.indexedDB, timeoutMs = STORAGE_ATTEMPT_TIMEOUT_MS): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!factory) { reject(new StorageFailure('unavailable', 'The IndexedDB API is absent from this page context right now.')); return; }
    let settled = false;
    let request: IDBOpenDBRequest;
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timer);
      reject(storageFailure(error));
    };
    const timer = globalThis.setTimeout(() => fail(new StorageFailure('timeout', 'Opening IndexedDB took too long.')), timeoutMs);
    try { request = factory.open(DB_NAME, DB_VERSION); }
    catch (error) { fail(error); return; }
    request.onblocked = () => fail(new StorageFailure('blocked', 'IndexedDB is blocked by another open ZYRON Voice session.'));
    request.onerror = (event) => { event.preventDefault(); fail(request.error); };
    request.onupgradeneeded = () => {
      try {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
        request.transaction?.addEventListener('abort', () => fail(request.transaction?.error ?? new StorageFailure('abort', 'IndexedDB upgrade was aborted.')), { once: true });
      } catch (error) { fail(error); }
    };
    request.onsuccess = () => {
      const database = request.result;
      if (settled) { try { database.close(); } catch { /* Late success after timeout. */ } return; }
      settled = true;
      globalThis.clearTimeout(timer);
      resolve(trackConnection(database));
    };
  });
}

export async function retryStorageOperation<T>(operation: () => Promise<T>, options: StorageRetryOptions = {}): Promise<T> {
  const maxAttempts = options.maxAttempts ?? STORAGE_MAX_ATTEMPTS;
  const attemptTimeoutMs = options.attemptTimeoutMs ?? STORAGE_ATTEMPT_TIMEOUT_MS;
  const retryDelayMs = options.retryDelayMs ?? 300;
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    options.onAttempt?.(attempt, maxAttempts);
    if (attempt > 1) closeDatabaseConnections();
    try { return await timeout(operation(), attemptTimeoutMs, `IndexedDB attempt ${attempt} timed out.`); }
    catch (error) {
      lastError = error;
      if (attempt < maxAttempts) await delay(retryDelayMs * attempt);
    }
  }
  throw storageFailure(lastError);
}

async function readStateOnce(): Promise<unknown> {
  const database = await openDatabase();
  try {
    return await new Promise<unknown>((resolve, reject) => {
      let settled = false;
      let value: unknown;
      let transaction: IDBTransaction | undefined;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timer);
        database.removeEventListener('close', connectionClosed);
        if (error) reject(storageFailure(error));
        else resolve(value);
      };
      const connectionClosed = () => finish(new StorageFailure('connection-lost', 'The IndexedDB connection closed unexpectedly.'));
      const timer = globalThis.setTimeout(() => {
        try { transaction?.abort(); } catch { /* WebKit already ended the transaction. */ }
        finish(new StorageFailure('timeout', 'Reading IndexedDB took too long.'));
      }, STORAGE_ATTEMPT_TIMEOUT_MS);
      database.addEventListener('close', connectionClosed, { once: true });
      try {
        const activeTransaction = database.transaction(STORE, 'readonly');
        transaction = activeTransaction;
        const request = activeTransaction.objectStore(STORE).get('app');
        request.onsuccess = () => { value = request.result; };
        request.onerror = (event) => { event.preventDefault(); finish(request.error); };
        activeTransaction.oncomplete = () => finish();
        activeTransaction.onerror = (event) => { event.preventDefault(); finish(activeTransaction.error); };
        activeTransaction.onabort = () => finish(activeTransaction.error ?? new StorageFailure('abort', 'Reading IndexedDB was aborted.'));
      } catch (error) { finish(error); }
    });
  } finally { releaseConnection(database); }
}

async function writeStateOnce(state: AppState): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let transaction: IDBTransaction | undefined;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timer);
        database.removeEventListener('close', connectionClosed);
        if (error) reject(storageFailure(error));
        else resolve();
      };
      const connectionClosed = () => finish(new StorageFailure('connection-lost', 'The IndexedDB connection closed before saving completed.'));
      const timer = globalThis.setTimeout(() => {
        try { transaction?.abort(); } catch { /* WebKit already ended the transaction. */ }
        finish(new StorageFailure('timeout', 'Saving IndexedDB took too long.'));
      }, STORAGE_ATTEMPT_TIMEOUT_MS);
      database.addEventListener('close', connectionClosed, { once: true });
      try {
        const activeTransaction = database.transaction(STORE, 'readwrite');
        transaction = activeTransaction;
        activeTransaction.objectStore(STORE).put(state, 'app');
        activeTransaction.oncomplete = () => finish();
        activeTransaction.onerror = (event) => { event.preventDefault(); finish(activeTransaction.error); };
        activeTransaction.onabort = () => finish(activeTransaction.error ?? new StorageFailure('abort', 'Saving IndexedDB was aborted.'));
      } catch (error) { finish(error); }
    });
  } finally { releaseConnection(database); }
}

function looksLikeState(value: unknown): value is Partial<AppState> {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<AppState>;
  return state.schemaVersion === 1 && !!state.settings
    && [state.emergency, state.bedroom, state.massage, state.tablets, state.people].every(Array.isArray)
    && typeof state.englishDraft === 'string' && typeof state.arabicDraft === 'string'
    && typeof state.settings.pinSalt === 'string' && typeof state.settings.pinHash === 'string';
}

async function hydrateState(value: unknown, legacyUpdatedAt = 0): Promise<AppState | null> {
  if (!looksLikeState(value)) return null;
  const base = await defaults();
  return {
    ...base,
    ...value,
    settings: { ...base.settings, ...value.settings },
    schemaVersion: 1,
    appVersion: typeof value.appVersion === 'string' ? value.appVersion : '1.0.x',
    updatedAt: Number.isFinite(value.updatedAt) ? Number(value.updatedAt) : legacyUpdatedAt,
    storageRevision: Number.isSafeInteger(value.storageRevision) && Number(value.storageRevision) >= 0 ? Number(value.storageRevision) : 0,
    recoveryBase: value.recoveryBase === true,
  };
}

async function readMirror(): Promise<StateMirror | null> {
  const raw = readMirrorRaw();
  if (!raw?.state) return null;
  const state = await hydrateState(raw.state, typeof raw.updatedAt === 'number' ? raw.updatedAt : 0);
  if (!state) return null;
  return {
    format: 2,
    state,
    baseHash: typeof raw.baseHash === 'string' ? raw.baseHash : null,
    pending: raw.pending === true,
    updatedAt: state.updatedAt,
  };
}

function logicalState(state: AppState): Omit<AppState, 'appVersion' | 'updatedAt' | 'storageRevision' | 'recoveryBase'> {
  return {
    schemaVersion: state.schemaVersion,
    settings: state.settings,
    emergency: state.emergency,
    bedroom: state.bedroom,
    massage: state.massage,
    tablets: state.tablets,
    people: state.people,
    englishDraft: state.englishDraft,
    arabicDraft: state.arabicDraft,
  };
}

async function stateHash(state: AppState): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(logicalState(state)));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToHex(new Uint8Array(digest));
}

function newest(first: AppState, second: AppState): AppState {
  if (first.storageRevision !== second.storageRevision) return first.storageRevision > second.storageRevision ? first : second;
  return first.updatedAt >= second.updatedAt ? first : second;
}

async function mirrorSnapshot(state: AppState, baseHash: string | null, pending: boolean): Promise<void> {
  writeMirror({ format: 2, state, baseHash, pending, updatedAt: state.updatedAt });
}

async function synchronized(state: AppState): Promise<ReconciliationResult> {
  const hash = await stateHash(state);
  await writeStateOnce(state);
  try { await mirrorSnapshot(state, hash, false); } catch { /* IndexedDB remains usable if the mirror is full or unavailable. */ }
  return { state, mode: 'indexeddb', changed: true };
}

async function reconcileSnapshots(current: AppState, mirror: StateMirror | null, primary: AppState): Promise<ReconciliationResult> {
  const local = mirror?.state ?? current;
  const [primaryHash, localHash] = await Promise.all([stateHash(primary), stateHash(local)]);
  if (primaryHash === localHash) {
    const selected = newest(primary, local);
    try { await mirrorSnapshot(selected, primaryHash, false); } catch { /* IndexedDB remains usable. */ }
    if (selected !== primary) await writeStateOnce(selected);
    return { state: selected, mode: 'indexeddb', changed: selected.updatedAt !== current.updatedAt || selected.storageRevision !== current.storageRevision };
  }

  if (local.recoveryBase) {
    try { await mirrorSnapshot(primary, primaryHash, false); } catch { /* IndexedDB remains authoritative. */ }
    return { state: primary, mode: 'indexeddb', changed: true, warning: 'Recovered your saved settings from the main database.' };
  }

  if (mirror?.pending && mirror.baseHash === primaryHash) return synchronized(local);
  if (!mirror?.pending) return synchronized(newest(primary, local));

  return {
    state: local,
    mode: 'fallback',
    changed: false,
    conflict: true,
    warning: 'The main database recovered with a different saved copy. Current communication data remains active and the recovered copy has not been overwritten.',
  };
}

type SettledPrimary = { status: 'fulfilled'; value: unknown } | { status: 'rejected'; reason: unknown } | { status: 'pending' };

function settleWithin(promise: Promise<unknown>, milliseconds: number): Promise<SettledPrimary> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = globalThis.setTimeout(() => { if (!settled) { settled = true; resolve({ status: 'pending' }); } }, milliseconds);
    promise.then(
      (value) => { if (!settled) { settled = true; globalThis.clearTimeout(timer); resolve({ status: 'fulfilled', value }); } },
      (reason) => { if (!settled) { settled = true; globalThis.clearTimeout(timer); resolve({ status: 'rejected', reason }); } },
    );
  });
}

export async function loadState(options: StorageRetryOptions = {}): Promise<LoadedState> {
  await saveQueue.catch(() => undefined);
  closeDatabaseConnections();
  const mirrorPromise = readMirror();
  const primaryPromise = retryStorageOperation(readStateOnce, options);
  void primaryPromise.catch(() => undefined);
  const mirror = await mirrorPromise;
  const quickPrimary = await settleWithin(primaryPromise, STARTUP_PRIMARY_GRACE_MS);
  if (quickPrimary.status === 'fulfilled') {
    const primary = await hydrateState(quickPrimary.value);
    if (primary && mirror) return reconcileSnapshots(mirror.state, mirror, primary);
    if (primary) {
      const hash = await stateHash(primary);
      try { await mirrorSnapshot(primary, hash, false); } catch { /* IndexedDB is still authoritative. */ }
      return { state: primary, mode: 'indexeddb' };
    }
    if (mirror) return synchronized(mirror.state);
    const state = await defaults();
    const stamped = { ...state, storageRevision: 1, updatedAt: Date.now() };
    await writeStateOnce(stamped);
    try { await mirrorSnapshot(stamped, await stateHash(stamped), false); } catch { /* First-run state still exists in IndexedDB. */ }
    return { state: stamped, mode: 'indexeddb' };
  }

  if (mirror) {
    return { state: mirror.state, mode: 'fallback', warning: 'Using the secondary local mirror. Communication remains available while the main database reconnects.' };
  }

  const state = await safeDefaultState();
  try { await mirrorSnapshot(state, null, true); } catch { /* Memory-only operation remains available. */ }
  return {
    state,
    mode: mirrorStorage() ? 'fallback' : 'temporary',
    warning: 'Using temporary local storage. Saved data will sync when the main database becomes available.',
  };
}

export async function reconcileStorage(current: AppState, options: StorageRetryOptions = {}): Promise<ReconciliationResult> {
  await saveQueue.catch(() => undefined);
  closeDatabaseConnections();
  const mirror = await readMirror();
  let primaryValue: unknown;
  try {
    primaryValue = await retryStorageOperation(readStateOnce, { maxAttempts: 1, attemptTimeoutMs: 1_500, retryDelayMs: 0, ...options });
  } catch {
    return {
      state: mirror?.state ?? current,
      mode: mirror ? 'fallback' : 'temporary',
      changed: false,
      warning: 'Using temporary local storage. Saved data will sync when the main database becomes available.',
    };
  }

  const primary = await hydrateState(primaryValue);
  const local = mirror?.state ?? current;
  if (!primary) return synchronized(local);
  return reconcileSnapshots(current, mirror, primary);
}

export function saveState(state: AppState, mode?: StorageMode, options: StorageRetryOptions = {}): Promise<LoadedState> {
  const operation = saveQueue.catch(() => undefined).then(() => saveStateOnce(state, mode ?? 'indexeddb', options));
  saveQueue = operation;
  return operation;
}

async function saveStateOnce(state: AppState, mode: StorageMode, options: StorageRetryOptions): Promise<LoadedState> {
  const previous = await readMirror();
  const previousState = previous?.state;
  const unchanged = previousState ? await stateHash(previousState) === await stateHash(state) : false;
  const stamped: AppState = unchanged && previousState
    ? { ...state, appVersion: APP_VERSION, updatedAt: previousState.updatedAt, storageRevision: previousState.storageRevision, recoveryBase: previousState.recoveryBase }
    : { ...state, appVersion: APP_VERSION, updatedAt: Date.now(), storageRevision: Math.max(state.storageRevision, previousState?.storageRevision ?? 0) + 1, recoveryBase: false };

  let mirrorSaved = false;
  try {
    await mirrorSnapshot(stamped, previous?.baseHash ?? null, mode !== 'indexeddb');
    mirrorSaved = true;
  } catch { /* IndexedDB may still save the state. */ }

  if (mode !== 'indexeddb') {
    return {
      state: stamped,
      mode: mirrorSaved ? 'fallback' : 'temporary',
      warning: mirrorSaved
        ? 'Using the secondary local mirror. Saved data will sync when the main database becomes available.'
        : 'Using temporary memory. Communication remains available, but changes cannot be persisted yet.',
    };
  }

  try {
    await retryStorageOperation(() => writeStateOnce(stamped), { maxAttempts: 2, ...options });
    try { await mirrorSnapshot(stamped, await stateHash(stamped), false); } catch { /* Primary save succeeded. */ }
    return { state: stamped, mode: 'indexeddb' };
  } catch {
    if (mirrorSaved) {
      try { await mirrorSnapshot(stamped, previous?.baseHash ?? null, true); } catch { /* The first mirror write already succeeded. */ }
    }
    return {
      state: stamped,
      mode: mirrorSaved ? 'fallback' : 'temporary',
      warning: mirrorSaved
        ? 'Using the secondary local mirror. Saved data will sync when the main database becomes available.'
        : 'Using temporary memory. Communication remains available, but changes cannot be persisted yet.',
    };
  }
}

export function resetLocalData(factory: IDBFactory | undefined = globalThis.indexedDB, timeoutMs = 5_000): Promise<void> {
  closeDatabaseConnections();
  return new Promise((resolve, reject) => {
    if (!factory) { reject(new StorageFailure('unavailable', 'IndexedDB is not available in this browser.')); return; }
    let settled = false;
    let request: IDBOpenDBRequest;
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timer);
      if (error) reject(storageFailure(error));
      else resolve();
    };
    const timer = globalThis.setTimeout(() => finish(new StorageFailure('timeout', 'Resetting local storage took too long. Close other ZYRON Voice windows and try again.')), timeoutMs);
    try { request = factory.deleteDatabase(DB_NAME); }
    catch (error) { finish(error); return; }
    request.onsuccess = () => {
      try { mirrorStorage()?.removeItem(MIRROR_KEY); finish(); }
      catch (error) { finish(error); }
    };
    request.onerror = (event) => { event.preventDefault(); finish(request.error); };
    request.onblocked = () => finish(new StorageFailure('blocked', 'Reset is blocked by another open ZYRON Voice session.'));
  });
}

export async function restoreCatalog(state: AppState): Promise<AppState> {
  return { ...state, emergency: structuredClone(defaultCatalog.emergency), bedroom: structuredClone(defaultCatalog.bedroom), massage: structuredClone(defaultCatalog.massage), tablets: structuredClone(defaultCatalog.tablets), people: structuredClone(defaultCatalog.people) };
}
